import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ConsolidatedMatrix } from '@/pages/finanzen/integrierteplanung/ConsolidatedMatrix.tsx'
import { CreateGroupModal } from '@/pages/finanzen/integrierteplanung/CreateGroupModal.tsx'
import { IntercompanyModal } from '@/pages/finanzen/integrierteplanung/IntercompanyModal.tsx'
import type { Group } from '@/pages/finanzen/integrierteplanung/konzern-shared.ts'
import { ChevronDown, ChevronRight, Database, Download, Info, Loader2, Percent, Plus, RefreshCw, Settings } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { SapDocumentView } from '@/components/sap/SapDocumentView.tsx'
import {
  Button,
  Card,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui'
import { ExportDialog } from './ExportDialog.tsx'
import { cn } from '@/lib/utils.ts'
import type {
  PlanComparisonResponse,
  PlanComparisonRow,
  PlanCellMonth,
  PlanSummary,
  AccountMappingRow,
  CoaAccount,
  BwaPosition,
  PlanAdjustment,
  SapOpenDocument,
} from './types.ts'
import { MappingTab } from './MappingTab.tsx'
import { RevenueGroupMappingTab } from './RevenueGroupMappingTab.tsx'
import { AdjustmentsTab } from './AdjustmentsTab.tsx'
import { PersonnelTab } from './PersonnelTab.tsx'
import { KonzernOverviewTab } from './KonzernOverviewTab.tsx'
import { ParametersTab } from './ParametersTab.tsx'

interface CacheStatus {
  cached: boolean
  lastUpdated: string | null
  cacheAge: string
  jeCount: number
  refreshActive: boolean
  lastError: { timestamp: string; message: string } | null
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

/**
 * Pro Zelle: ist der Monat abgeschlossen (→ IST aus SAP) oder noch offen
 * (→ SOLL aus Plan)? Die Grenze ist der erste Tag des aktuellen Monats —
 * der laufende Monat zaehlt noch nicht als „abgeschlossen", weil das
 * Geschaeftsmonatsergebnis erst nach Buchungsschluss vergleichbar ist.
 */
function isMonthClosed(planYear: number, month: number): boolean {
  const now = new Date()
  if (planYear < now.getFullYear()) return true
  if (planYear > now.getFullYear()) return false
  return month < now.getMonth() + 1
}

/**
 * Wie wird der Jahreswert einer Position aggregiert?
 *  - 'sum'   = Summe der 12 Monate (Default — passt fuer Flows: Cashflow, GuV)
 *  - 'first' = Wert des ersten Monats (Bestaende am Jahresanfang, z.B. Anfangsbestand)
 *  - 'last'  = Wert des letzten Monats (Bestaende am Jahresende, z.B. Endbestand)
 */
type AggregateMode = 'sum' | 'first' | 'last'

const AGGREGATE_MODE_BY_CODE: Record<string, AggregateMode> = {
  liq_anfangsbestand: 'first',
  liq_endbestand: 'last',
}

function getAggregateMode(code: string): AggregateMode {
  // Bilanzpositionen sind Bestaende -> Jahreswert = Dezember-Saldo ('last').
  if (code.startsWith('bs_')) return 'last'
  return AGGREGATE_MODE_BY_CODE[code] ?? 'sum'
}

const AGGREGATE_LABELS: Record<AggregateMode, { letter: string; tooltip: string }> = {
  sum:   { letter: 'S', tooltip: 'S = Summe aller 12 Monatswerte' },
  first: { letter: 'F', tooltip: 'F = First — Wert des ersten Monats (Januar). Sinnvoll für Bestände am Jahresanfang.' },
  last:  { letter: 'L', tooltip: 'L = Last — Wert des letzten Monats (Dezember). Sinnvoll für Bestände am Jahresende.' },
}

/**
 * Jahres-Aggregate fuer eine Plan-Zeile abhaengig vom Modus:
 *  - sum:   IST = YTD-Summe der abgeschlossenen Monate, SOLL = Jahres-Summe (12 Monate),
 *           Delta = Summe der Deltas der abgeschlossenen Monate.
 *  - first: Werte aus Januar (Anfangsbestand zu Jahresanfang).
 *  - last:  Werte aus Dezember (Endbestand zu Jahresende).
 */
function annualTotals(monthly: PlanCellMonth[], mode: AggregateMode): {
  soll: number
  ist: number | null
  delta: number | null
} {
  if (mode === 'first') {
    const c = monthly[0]
    if (!c) return { soll: 0, ist: null, delta: null }
    return { soll: c.soll ?? 0, ist: c.ist, delta: c.delta }
  }
  if (mode === 'last') {
    const c = monthly[11] ?? monthly[monthly.length - 1]
    if (!c) return { soll: 0, ist: null, delta: null }
    return { soll: c.soll ?? 0, ist: c.ist, delta: c.delta }
  }
  let soll = 0
  let ist = 0
  let istSollClosed = 0
  let hasIst = false
  for (const c of monthly) {
    soll += c.soll ?? 0
    if (c.ist !== null) {
      ist += c.ist
      istSollClosed += c.soll ?? 0
      hasIst = true
    }
  }
  return {
    soll,
    ist: hasIst ? ist : null,
    delta: hasIst ? ist - istSollClosed : null,
  }
}

/**
 * Bezugsgroesse fuer die %-Anzeige pro Position.
 *  - 'umsatz' = Umsatzerloese (Umsatzkostenquote / Margen / Bestandteile)
 *  - 'ebt'    = EBT (nur Steuerquote)
 *
 * Aggregat-Hierarchie (Umsatzerloese selbst, Aggregate Cashflow / Liquiditaet)
 * bekommt KEIN Prozent — Umsatz waere immer 100%, Cashflow/Liqui haben
 * keinen sinnvollen Bezug.
 */
const PERCENT_REFERENCE: Record<string, 'umsatz' | 'ebt'> = {
  // Umsatz-Bestandteile (Anteil am Gesamtumsatz)
  ums_erloese_gl: 'umsatz',
  ums_vertraege: 'umsatz',
  ums_dienstleistungen: 'umsatz',
  ums_auftragsbestand: 'umsatz',
  ums_angebotsbestand: 'umsatz',
  ums_aquise_bestand: 'umsatz',
  ums_aquise_neu: 'umsatz',
  // GuV — Margen / Kostenquoten
  wareneinsatz: 'umsatz',
  rohertrag: 'umsatz',
  sonstige_ertraege: 'umsatz',
  personalkosten: 'umsatz',
  raumkosten: 'umsatz',
  kfz_kosten: 'umsatz',
  werbe_reisekosten: 'umsatz',
  reparaturen: 'umsatz',
  sonstige_kosten: 'umsatz',
  versicherungen: 'umsatz',
  kfz_steuer: 'umsatz',
  ebitda: 'umsatz',
  afa: 'umsatz',
  ebit: 'umsatz',
  zinsaufwand: 'umsatz',
  zinsertraege: 'umsatz',
  ebt: 'umsatz',
  steuern: 'ebt',
  jahresueberschuss: 'umsatz',
}

const PCT = new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 1, minimumFractionDigits: 0 })

/**
 * Liefert „60 %" fuer value/base, oder null wenn die Bezugsgroesse 0/leer ist.
 * Negative Werte werden vorzeichenrichtig dargestellt (Wareneinsatz = +60 %,
 * also als positiver Anteil — Vorzeichen wird im Frontend bereits ueber das
 * BWA-Vorzeichen normalisiert).
 */
function formatPercent(value: number | null, base: number | null): string | null {
  if (value === null || base === null) return null
  if (!Number.isFinite(base) || base === 0) return null
  const ratio = value / base
  if (!Number.isFinite(ratio)) return null
  return PCT.format(ratio)
}

/** Summe einer Drilldown-Zeile (Konto / Partner) ohne Null-Werte. */
function annualSum(monthly: (number | null)[]): number | null {
  let sum = 0
  let hasValue = false
  for (const v of monthly) {
    if (v !== null) {
      sum += v
      hasValue = true
    }
  }
  return hasValue ? sum : null
}

// Zentraler Pfad fuer die Konzern-Sicht (group-Scope). Konzern lebt unter der
// integrierten Planung; Gruppen-Auswahl navigiert immer hierher.
const KONZERN_BASE = '/finanzen/integrierteplanung/konzern'

export function PlanEditorPage({ basePath = '/finanzen/liquiditaet/planung', scope = 'company' }: { basePath?: string; scope?: 'company' | 'group' } = {}) {
  const { planId, groupId } = useParams<{ planId: string; groupId: string }>()
  const navigate = useNavigate()
  const id = Number(planId)
  const isGroupScope = scope === 'group'
  const { toast } = useToast()

  const [plan, setPlan] = useState<PlanSummary | null>(null)
  const [companies, setCompanies] = useState<{ key: string; name: string }[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [icOpen, setIcOpen] = useState(false)
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [editGroup, setEditGroup] = useState<Group | null>(null)
  const [scopeReloadToken, setScopeReloadToken] = useState(0)
  const [comparison, setComparison] = useState<PlanComparisonResponse | null>(null)
  const [adjustments, setAdjustments] = useState<PlanAdjustment[]>([])
  const [positions, setPositions] = useState<BwaPosition[]>([])
  const [mapping, setMapping] = useState<AccountMappingRow[]>([])
  const [coa, setCoa] = useState<CoaAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<string | null>(null)
  const [recalcing, setRecalcing] = useState(false)
  const [factorInput, setFactorInput] = useState('0')
  const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null)
  const [refreshingCache, setRefreshingCache] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  // Einstellungs-Modals: Parameter (%-Werte / Quoten) und Konten-Mapping
  // sind keine Hauptansichten — sie werden ueber den Zahnrad-Dropdown
  // oben rechts geoeffnet, statt die Tab-Bar zu fluten.
  const [settingsModal, setSettingsModal] = useState<'parameters' | 'mapping' | 'warengruppen' | null>(null)
  const [exportOpen, setExportOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setErrorDetails(null)
    // Sequenziell laden, damit der erste Fehler exakt beim verursachenden
    // Endpoint stehen bleibt — Promise.all versteckt, welcher Call 500't.
    const stage = async <T,>(label: string, p: Promise<T>): Promise<T> => {
      try {
        return await p
      } catch (err) {
        const e = err as { response?: { status?: number; data?: unknown }; config?: { url?: string; method?: string }; message?: string }
        const status = e?.response?.status
        const body = e?.response?.data
        const url = e?.config?.url
        const method = (e?.config?.method ?? 'get').toUpperCase()
        // Stack aus dem Response-Body separat extrahieren (siehe errorHandler.ts) —
        // ohne Truncation, damit der User die exakte Stelle des Fehlers sieht.
        const bodyObj = body && typeof body === 'object' ? body as Record<string, unknown> : null
        const stack = bodyObj && typeof bodyObj['stack'] === 'string' ? bodyObj['stack'] : null
        const bodyForDisplay: unknown = bodyObj && stack
          ? Object.fromEntries(Object.entries(bodyObj).filter(([k]) => k !== 'stack'))
          : body
        const bodyStr = bodyForDisplay
          ? typeof bodyForDisplay === 'string' ? bodyForDisplay : JSON.stringify(bodyForDisplay)
          : ''
        const detail = [
          `Schritt: ${label}`,
          `Request: ${method} ${url ?? '?'}`,
          `Status: ${status ?? '?'}`,
          bodyStr ? `Body: ${bodyStr.slice(0, 800)}` : '',
          e?.message ? `Message: ${e.message}` : '',
          stack ? `\nStack:\n${stack}` : '',
        ].filter(Boolean).join('\n')
        setErrorDetails(detail)
        throw err
      }
    }
    try {
      const planRes = await stage(`GET /api/liquidity-planning/plans/${id}`,
        apiClient.get<{ plan: PlanSummary; cells: unknown[]; adjustments: PlanAdjustment[] }>(`/liquidity-planning/plans/${id}`))
      const posRes = await stage('GET /api/liquidity-planning/positions',
        apiClient.get<BwaPosition[]>('/liquidity-planning/positions'))
      const compRes = await stage('GET /api/liquidity-planning/companies',
        apiClient.get<{ key: string; name: string }[]>('/liquidity-planning/companies'))
      setPlan(planRes.data.plan)
      setAdjustments(planRes.data.adjustments)
      setPositions(posRes.data)
      setCompanies(compRes.data ?? [])
      setFactorInput(String(planRes.data.plan.baseFactorPercent))

      const cmpRes = await stage(`GET /api/liquidity-planning/plans/${id}/comparison`,
        apiClient.get<PlanComparisonResponse>(`/liquidity-planning/plans/${id}/comparison`))
      const mapRes = await stage(`GET /api/liquidity-planning/mapping?company=${planRes.data.plan.companyKey}`,
        apiClient.get<AccountMappingRow[]>(`/liquidity-planning/mapping?company=${planRes.data.plan.companyKey}`))
      const coaRes = await stage(`GET /api/liquidity-planning/coa?company=${planRes.data.plan.companyKey}`,
        apiClient.get<{ accounts: CoaAccount[]; hint?: string }>(`/liquidity-planning/coa?company=${planRes.data.plan.companyKey}`))
      setComparison(cmpRes.data)
      setMapping(mapRes.data)
      setCoa(coaRes.data.accounts)

      // Cache-Status best-effort laden — fehlt z.B. wenn der Endpoint noch
      // nicht deployed ist; das soll den Plan-Editor nicht blockieren.
      try {
        const statusRes = await apiClient.get<CacheStatus>(
          `/liquidity-planning/cache-status?company=${planRes.data.plan.companyKey}`,
        )
        setCacheStatus(statusRes.data)
      } catch {
        setCacheStatus(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }, [id])

  // Im company-Scope laedt load() den Plan; im group-Scope gibt es keinen Plan.
  useEffect(() => {
    if (isGroupScope) { setLoading(false); return }
    void load()
  }, [load, isGroupScope])

  // Scope-Selektor-Daten laden (Best-effort, blockiert den Editor nicht):
  // Konzern-Gruppen immer; Gesellschaften nur im group-Scope, weil sie im
  // company-Scope bereits von load() geholt werden (kein Doppel-Fetch).
  useEffect(() => {
    void (async () => {
      try {
        const yr = new Date().getFullYear()
        const grpPromise = apiClient.get<Group[]>(`/liquidity-planning/consolidation/groups?year=${yr}`)
        if (isGroupScope) {
          const [compRes, grpRes] = await Promise.all([
            apiClient.get<{ key: string; name: string }[]>('/liquidity-planning/companies'),
            grpPromise,
          ])
          setCompanies(compRes.data ?? [])
          setGroups(Array.isArray(grpRes.data) ? grpRes.data : [])
        } else {
          const grpRes = await grpPromise
          setGroups(Array.isArray(grpRes.data) ? grpRes.data : [])
        }
      } catch {
        /* Selektor-Daten sind nicht kritisch fuer die Hauptansicht */
      }
    })()
  }, [isGroupScope, scopeReloadToken])

  const selectedGroupId = groupId ? Number(groupId) : (groups[0]?.id ?? null)

  function selectScopeCompany(companyKey: string) {
    void switchTo(companyKey, plan?.year ?? new Date().getFullYear())
  }
  function selectScopeGroup(gid: number) {
    navigate(`${KONZERN_BASE}/${gid}`)
  }

  async function refreshComparison() {
    if (!plan) return
    const res = await apiClient.get<PlanComparisonResponse>(`/liquidity-planning/plans/${id}/comparison`)
    setComparison(res.data)
  }

  async function reloadMapping() {
    if (!plan) return
    const res = await apiClient.get<AccountMappingRow[]>(`/liquidity-planning/mapping?company=${plan.companyKey}`)
    setMapping(res.data)
  }

  async function handleRecalc() {
    setRecalcing(true)
    try {
      const res = await apiClient.post<{ ok: boolean; usedPlanFallback?: number[] }>(
        `/liquidity-planning/plans/${id}/recalc`,
        {},
      )
      await refreshComparison()
      const fallbackYears = res.data?.usedPlanFallback ?? []
      const description = fallbackYears.length > 0
        ? `Hinweis: ${fallbackYears.join(', ')} hatte keine SAP-Daten — Berechnung basiert auf den ${fallbackYears.join('/')}er Planwerten.`
        : undefined
      toast({
        title: `Plan ${plan?.year ?? ''} neu berechnet`,
        description,
        tone: 'success',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Recalc fehlgeschlagen')
    } finally {
      setRecalcing(false)
    }
  }

  // SAP-Cache-Refresh fuer den aktuellen Mandanten — geht ueber den zentralen
  // Adapter (lib/liquidity/cache-refresh.ts). Nach erfolgreichem Refresh
  // werden Cache-Status, Plan-Recalc und Vergleich neu geladen.
  async function handleSapRefresh() {
    if (!plan) return
    setRefreshingCache(true)
    setRefreshError(null)
    try {
      await apiClient.post(`/liquidity-planning/refresh-cache?company=${plan.companyKey}`, {})
      try {
        const statusRes = await apiClient.get<CacheStatus>(
          `/liquidity-planning/cache-status?company=${plan.companyKey}`,
        )
        setCacheStatus(statusRes.data)
      } catch {
        // non-fatal
      }
      await apiClient.post(`/liquidity-planning/plans/${id}/recalc`, {})
      await refreshComparison()
    } catch (err) {
      setRefreshError(err instanceof Error ? err.message : 'SAP-Aktualisierung fehlgeschlagen')
    } finally {
      setRefreshingCache(false)
    }
  }

  async function handleFactorChange(value: number) {
    if (!plan) return
    await apiClient.put(`/liquidity-planning/plans/${id}`, { baseFactorPercent: value })
    await load()
  }

  async function handleCellOverride(positionCode: string, month: number, override: number | null) {
    await apiClient.put(`/liquidity-planning/plans/${id}/cells/${positionCode}/${month}`, {
      overrideValue: override,
    })
    await refreshComparison()
  }

  /**
   * Setzt den selben Override-Wert fuer mehrere Monate (z.B. "ab Mai bis Dez")
   * in einem einzigen Backend-Request. Refresh erfolgt nur einmal am Ende.
   */
  async function handleCellOverrideBulk(positionCode: string, months: number[], override: number | null) {
    if (months.length === 0) return
    await apiClient.put(`/liquidity-planning/plans/${id}/cells/${positionCode}/bulk`, {
      months,
      overrideValue: override,
    })
    await refreshComparison()
  }

  // Wechselt zu einem anderen Mandanten oder Jahr — nutzt den
  // Get-or-Create-Endpoint, sodass Plan automatisch existiert.
  async function switchTo(companyKey: string, year: number) {
    setLoading(true)
    try {
      const res = await apiClient.get<{ planId: number }>(
        `/liquidity-planning/plans/active?company=${encodeURIComponent(companyKey)}&year=${year}`,
      )
      navigate(`${basePath}/${res.data.planId}`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wechsel fehlgeschlagen')
      setLoading(false)
    }
  }

  const matrix = comparison?.matrix ?? []
  const currentYear = new Date().getFullYear()
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2]

  // Gemeinsamer Scope-Selektor: Einzelgesellschaft ODER Konzern-Gruppe.
  // value-Prefix unterscheidet die Auswahl (company:KEY / group:ID).
  function renderScopeSelect() {
    const value = isGroupScope
      ? (selectedGroupId !== null ? `group:${selectedGroupId}` : '')
      : (plan ? `company:${plan.companyKey}` : '')
    return (
      <div className="flex flex-col gap-1">
        <label className="text-small font-medium text-gray-700">Ansicht</label>
        <SelectRoot
          value={value}
          onValueChange={(v) => {
            if (v.startsWith('group:')) selectScopeGroup(Number(v.slice('group:'.length)))
            else if (v.startsWith('company:')) selectScopeCompany(v.slice('company:'.length))
          }}
        >
          <SelectTrigger className="min-w-[280px]">
            <SelectValue placeholder="Gesellschaft oder Konzern wählen" />
          </SelectTrigger>
          <SelectContent>
            {groups.map((g) => (
              <SelectItem key={`g-${g.id}`} value={`group:${g.id}`}>Konzern: {g.name}</SelectItem>
            ))}
            {companies.filter((c) => Boolean(c.key)).map((c) => (
              <SelectItem key={`c-${c.key}`} value={`company:${c.key}`}>{c.key} – {c.name}</SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      </div>
    )
  }

  // ── Konzern-Scope: konsolidierte Sicht statt Einzelplan ────────────────────
  if (isGroupScope) {
    return (
      <div className="space-y-4 p-6">
        <PageHeader
          title="Integrierte Planung – Konzern"
          breadcrumbs={[{ label: 'Finanzen' }, { label: 'Integrierte Planung' }, { label: 'Konzern' }]}
          actions={
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setIcOpen(true)}>Intercompany verwalten</Button>
              <Button
                variant="secondary"
                disabled={selectedGroupId === null}
                onClick={() => { setEditGroup(groups.find((g) => g.id === selectedGroupId) ?? null); setGroupModalOpen(true) }}
              >
                Firmen bearbeiten
              </Button>
              <Button variant="primary" onClick={() => { setEditGroup(null); setGroupModalOpen(true) }}>
                <Plus className="size-4 mr-1" /> Konzern-Gruppe
              </Button>
            </div>
          }
        />

        <Card>
          <Card.Body>
            <div className="flex items-end gap-3 flex-wrap">
              {renderScopeSelect()}
            </div>
          </Card.Body>
        </Card>

        {groups.length === 0 ? (
          <EmptyState
            title="Noch keine Konzern-Gruppe"
            description="Lege eine Gruppe an und wähle die Gesellschaften, die konsolidiert werden sollen."
          />
        ) : selectedGroupId !== null ? (
          <Tabs defaultValue="matrix">
            <TabsList>
              <TabsTrigger value="matrix">Konzern-Matrix</TabsTrigger>
              <TabsTrigger value="overview">Liquiditäts-Forecast (SAP)</TabsTrigger>
            </TabsList>
            <TabsContent value="matrix">
              <ConsolidatedMatrix groupId={selectedGroupId} reloadToken={scopeReloadToken} />
            </TabsContent>
            <TabsContent value="overview">
              <KonzernOverviewTab />
            </TabsContent>
          </Tabs>
        ) : null}

        <CreateGroupModal
          open={groupModalOpen}
          onOpenChange={setGroupModalOpen}
          year={currentYear}
          companies={companies}
          group={editGroup}
          onSaved={(gid) => { setGroupModalOpen(false); setScopeReloadToken((t) => t + 1); navigate(`${KONZERN_BASE}/${gid}`) }}
        />
        <IntercompanyModal
          open={icOpen}
          onOpenChange={setIcOpen}
          year={currentYear}
          companies={companies}
          onChanged={() => setScopeReloadToken((t) => t + 1)}
        />
      </div>
    )
  }

  if (loading) return <div className="p-6"><LoadingState label="Lade Plan…" /></div>
  if (error) return <div className="p-6"><ErrorState title="Fehler" description={error} onRetry={load} details={errorDetails ?? undefined} /></div>
  if (!plan || !comparison) return null

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Liquiditätsplanung"
        breadcrumbs={[
          { label: 'Finanzen' },
          { label: 'Liquiditätsplanung' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {cacheStatus && (
              <span className="text-caption text-gray-500 inline-flex items-center gap-1.5">
                <Database className="size-3.5" />
                {cacheStatus.cached
                  ? <>SAP-Daten: {cacheStatus.cacheAge}</>
                  : <span className="text-amber-700">Kein SAP-Cache</span>}
              </span>
            )}
            <Button
              variant="secondary"
              onClick={() => void handleSapRefresh()}
              disabled={refreshingCache || cacheStatus?.refreshActive}
              title="Lädt aktuelle SAP-Daten für diesen Mandanten und rechnet den Plan neu durch"
            >
              <RefreshCw className={cn('size-4 mr-1', refreshingCache && 'animate-spin')} />
              SAP aktualisieren
            </Button>
            <Button
              variant="secondary"
              onClick={handleRecalc}
              disabled={recalcing}
              title={`Berechnet den Plan für ${plan.year} neu. Andere Jahre sind nicht betroffen. SAP-Daten werden dabei NICHT neu abgerufen.`}
            >
              <RefreshCw className={cn('size-4 mr-1', recalcing && 'animate-spin')} />
              Neu berechnen
            </Button>
            <Button
              variant="secondary"
              title="Plan exportieren (Zeitraum, Excel/PDF, Standard/Banken-Layout)"
              onClick={() => setExportOpen(true)}
            >
              <Download className="size-4 mr-1" />
              Export
            </Button>
            <Dropdown>
              <DropdownTrigger asChild>
                <Button variant="secondary" title="Einstellungen: Parameter und Konten-Mapping">
                  <Settings className="size-4 mr-1" />
                  Einstellungen
                  <ChevronDown className="size-3.5 ml-1" />
                </Button>
              </DropdownTrigger>
              <DropdownContent align="end">
                <DropdownItem onSelect={() => setSettingsModal('parameters')}>
                  <Percent className="size-4" />
                  <div className="flex flex-col">
                    <span>Parameter</span>
                    <span className="text-caption text-gray-500">%-Werte / Quoten für die Plan-Berechnung</span>
                  </div>
                </DropdownItem>
                <DropdownItem onSelect={() => setSettingsModal('mapping')}>
                  <Settings className="size-4" />
                  <div className="flex flex-col">
                    <span>Konten-Mapping ({mapping.length})</span>
                    <span className="text-caption text-gray-500">SAP-Konten den BWA-Positionen zuordnen</span>
                  </div>
                </DropdownItem>
                <DropdownItem onSelect={() => setSettingsModal('warengruppen')}>
                  <Settings className="size-4" />
                  <div className="flex flex-col">
                    <span>Warengruppen-Mapping</span>
                    <span className="text-caption text-gray-500">Warengruppen den Umsatz-Positionen zuordnen</span>
                  </div>
                </DropdownItem>
              </DropdownContent>
            </Dropdown>
          </div>
        }
      />

      {refreshError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-small text-red-800">
          SAP-Aktualisierung fehlgeschlagen: {refreshError}
        </div>
      )}

      {cacheStatus?.lastError && !cacheStatus.cached && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-small text-amber-800">
          <span className="font-medium">Letzter Refresh-Versuch fehlgeschlagen</span> ({new Date(cacheStatus.lastError.timestamp).toLocaleString('de-DE')}):
          <span className="font-mono ml-1 break-all">{cacheStatus.lastError.message}</span>
        </div>
      )}

      <Card>
        <Card.Body>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="flex items-end gap-3 flex-wrap">
              {renderScopeSelect()}
              <div className="flex flex-col gap-1">
                <label className="text-small font-medium text-gray-700">Jahr</label>
                <SelectRoot value={String(plan.year)} onValueChange={(v) => switchTo(plan.companyKey, Number(v))}>
                  <SelectTrigger className="min-w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              </div>
              <Input
                label="Faktor (%)"
                type="number" step="0.5"
                value={factorInput}
                onChange={(e) => setFactorInput(e.target.value)}
                onBlur={() => handleFactorChange(Number(factorInput))}
                className="w-28"
              />
            </div>
            <div className="text-caption text-gray-500 max-w-md text-right">
              <div className="flex items-center gap-3 justify-end mb-1">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block size-2.5 rounded-full bg-gray-400" /> abgeschlossener Monat = IST aus SAP
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block size-2.5 rounded-full bg-blue-500" /> offener Monat = SOLL/Plan
                </span>
              </div>
              Basis: Mittel der letzten {plan.historyYears} Vorjahre × Saison × ({plan.baseFactorPercent >= 0 ? '+' : ''}{plan.baseFactorPercent.toFixed(2)} %)
            </div>
          </div>
        </Card.Body>
      </Card>

      <Tabs defaultValue="matrix">
        <TabsList>
          <TabsTrigger value="matrix">Plan-Matrix ({plan.companyKey})</TabsTrigger>
          <TabsTrigger value="overview">Liquiditäts-Forecast (SAP)</TabsTrigger>
          <TabsTrigger value="adjustments">
            Einzelpositionen ({adjustments.length})
          </TabsTrigger>
          <TabsTrigger value="personnel">Personal</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <KonzernOverviewTab />
        </TabsContent>

        <TabsContent value="matrix">
          <PlanMatrix
            rows={matrix}
            year={plan.year}
            planId={id}
            onCellOverride={handleCellOverride}
            onCellOverrideBulk={handleCellOverrideBulk}
            onRecalcDone={refreshComparison}
          />
        </TabsContent>

        <TabsContent value="adjustments">
          <AdjustmentsTab
            planId={id}
            year={plan.year}
            positions={positions}
            adjustments={adjustments}
            onChange={() => void load()}
          />
        </TabsContent>

        <TabsContent value="personnel">
          <PersonnelTab planId={id} year={plan.year} />
        </TabsContent>
      </Tabs>

      {/* Einstellungs-Modals (oben rechts via Dropdown geoeffnet) */}
      <Modal
        open={settingsModal === 'parameters'}
        onOpenChange={(open) => !open && setSettingsModal(null)}
        title="Parameter"
        description="Globale %-Werte und Quoten für die Plan-Berechnung."
        size="full"
      >
        <ParametersTab
          planId={id}
          onSaved={() => void load()}
        />
      </Modal>

      <Modal
        open={settingsModal === 'mapping'}
        onOpenChange={(open) => !open && setSettingsModal(null)}
        title={`Konten-Mapping (${mapping.length})`}
        description="SAP-Konten den BWA-Positionen zuordnen — Drag & Drop oder Vorschläge übernehmen."
        size="full"
      >
        <MappingTab
          companyKey={plan.companyKey}
          positions={positions}
          mapping={mapping}
          coa={coa}
          onSaved={() => void reloadMapping()}
        />
      </Modal>

      <Modal
        open={settingsModal === 'warengruppen'}
        onOpenChange={(open) => !open && setSettingsModal(null)}
        title="Warengruppen-Mapping"
        description="Warengruppen den Umsatz-Positionen zuordnen (Verträge / voraussichtliche Dienstleistungen …)."
        size="full"
      >
        <RevenueGroupMappingTab
          companyKey={plan.companyKey}
          positions={positions}
        />
      </Modal>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        companyKey={plan.companyKey}
        anchorYear={plan.year}
      />

    </div>
  )
}

interface InvoiceSummary {
  DocEntry: number
  DocNum: number
  DocDate: string
  DocDueDate?: string
  DocTotal: number
  DocCurrency: string
  DocumentStatus: string
  type: 'invoice' | 'credit'
}

const EUR_INV = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })

function PlanMatrix({
  rows, year, planId, onCellOverride, onCellOverrideBulk, onRecalcDone,
}: {
  rows: PlanComparisonRow[]
  year: number
  planId: number
  onCellOverride: (positionCode: string, month: number, override: number | null) => Promise<void>
  onCellOverrideBulk: (positionCode: string, months: number[], override: number | null) => Promise<void>
  onRecalcDone?: () => Promise<void>
}) {
  const [editing, setEditing] = useState<{ position: string; month: number; value: string } | null>(null)
  const [editError, setEditError] = useState<string | null>(null)
  const [recalcingMonth, setRecalcingMonth] = useState<number | null>(null)
  const [recalcingPosition, setRecalcingPosition] = useState<string | null>(null)
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const { toast } = useToast()

  async function handleMonthRecalc(month: number) {
    setRecalcingMonth(month)
    try {
      await apiClient.post(`/liquidity-planning/plans/${planId}/recalc/month/${month}`, {})
      await onRecalcDone?.()
      toast({ title: `Monat ${month} neu berechnet`, tone: 'success' })
    } catch (err) {
      const e = err as { response?: { data?: unknown }; message?: string }
      setEditError(`Monats-Recalc fehlgeschlagen: ${e?.message ?? 'Unbekannt'}`)
    } finally {
      setRecalcingMonth(null)
    }
  }

  async function handlePositionRecalc(positionCode: string) {
    setRecalcingPosition(positionCode)
    try {
      await apiClient.post(`/liquidity-planning/plans/${planId}/recalc/position/${positionCode}`, {})
      await onRecalcDone?.()
      toast({ title: 'Position neu berechnet', tone: 'success' })
    } catch (err) {
      const e = err as { response?: { data?: unknown }; message?: string }
      setEditError(`Positions-Recalc fehlgeschlagen: ${e?.message ?? 'Unbekannt'}`)
    } finally {
      setRecalcingPosition(null)
    }
  }
  // Schliesst den Editor und persistiert das Override. Bei API-Fehler wird die
  // Meldung als Banner ueber der Tabelle angezeigt — sonst wuerde der User
  // ohne Hinweis im Editor "haengenbleiben" (onBlur fired silently).
  async function commitEdit(positionCode: string, month: number, raw: string) {
    const v = raw === '' ? null : Number(raw)
    if (raw !== '' && Number.isNaN(v as number)) {
      setEditError(`„${raw}" ist keine gueltige Zahl.`)
      setEditing(null)
      return
    }
    try {
      setEditError(null)
      await onCellOverride(positionCode, month, v)
      setEditing(null)
    } catch (err) {
      reportEditError(err)
    }
  }

  /**
   * Speichert den Wert fuer den aktuellen Monat UND alle folgenden Monate
   * im Planjahr — z.B. bei Personalkosten ab Mai bis Dez gleicher Wert.
   * Gespart sich das einzelne Editieren der 8 Folgemonate.
   */
  /**
   * Speichert den Wert fuer einen Monatsbereich [fromMonth..toMonth]
   * (Default: bis Dezember). Genutzt fuer die Bulk-Buttons unter dem
   * Edit-Feld ("→ naechster Monat" und "→ Dez") sowie Shift+Enter.
   */
  async function commitEditFill(
    positionCode: string,
    fromMonth: number,
    raw: string,
    toMonth = 12,
  ) {
    const v = raw === '' ? null : Number(raw)
    if (raw !== '' && Number.isNaN(v as number)) {
      setEditError(`„${raw}" ist keine gueltige Zahl.`)
      setEditing(null)
      return
    }
    const months: number[] = []
    for (let m = fromMonth; m <= toMonth; m++) months.push(m)
    try {
      setEditError(null)
      await onCellOverrideBulk(positionCode, months, v)
      setEditing(null)
    } catch (err) {
      reportEditError(err)
    }
  }

  function reportEditError(err: unknown) {
    const e = err as { response?: { status?: number; data?: unknown }; message?: string }
    const status = e?.response?.status ?? '?'
    const body = e?.response?.data
    const bodyStr = body
      ? typeof body === 'string' ? body : JSON.stringify(body).slice(0, 300)
      : ''
    setEditError(
      `Speichern fehlgeschlagen (HTTP ${status}): ${e?.message ?? 'unbekannt'}${bodyStr ? ` · ${bodyStr}` : ''}`,
    )
    setEditing(null)
  }
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set())
  const [expandedPartners, setExpandedPartners] = useState<Set<string>>(new Set())
  // invoiceData: key = partnerCode, value = all invoices for the full plan year
  const [invoiceData, setInvoiceData] = useState<Map<string, InvoiceSummary[]>>(new Map())
  const [invoiceLoading, setInvoiceLoading] = useState<Set<string>>(new Set())
  const [invoiceModal, setInvoiceModal] = useState<{ doc: Record<string, unknown>; type: 'invoice' | 'credit' } | null>(null)
  const [invoiceModalLoading, setInvoiceModalLoading] = useState(false)
  const [sapDocModal, setSapDocModal] = useState<{ doc: Record<string, unknown>; type: 'order' | 'quotation' } | null>(null)
  const [sapDocModalLoading, setSapDocModalLoading] = useState(false)
  const loadedRef = useRef<Set<string>>(new Set())

  const sections = useMemo(() => {
    const guv = rows.filter((r) => r.section === 'guv')
    const bilanz = rows.filter((r) => r.section === 'bilanz')
    const cf = rows.filter((r) => r.section === 'cashflow')
    const li = rows.filter((r) => r.section === 'liqui')
    return { guv, bilanz, cf, li }
  }, [rows])

  // Bezugsbasen fuer die %-Anzeige (pro Monat + Jahres-Aggregat).
  // Werden einmal pro Render aus den Aggregate-Zeilen abgeleitet; die Map
  // unten greift dann pro Position auf 'umsatz' oder 'ebt' zu.
  const referenceBases = useMemo(() => {
    const umsatzRow = rows.find((r) => r.code === 'umsatzerloese')
    const ebtRow = rows.find((r) => r.code === 'ebt')
    const monthlyBases = (row: PlanComparisonRow | undefined) => ({
      soll: row ? row.monthly.map((c) => c.soll ?? 0) : new Array<number>(12).fill(0),
      ist: row ? row.monthly.map((c) => c.ist) : new Array<number | null>(12).fill(null),
    })
    return {
      umsatz: monthlyBases(umsatzRow),
      ebt: monthlyBases(ebtRow),
    }
  }, [rows])

  function toggleExpand(code: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  function toggleAccount(key: string) {
    setExpandedAccounts((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function togglePartner(key: string) {
    setExpandedPartners((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const loadPartnerInvoices = useCallback(async (partnerKey: string) => {
    if (loadedRef.current.has(partnerKey)) return
    loadedRef.current.add(partnerKey)
    setInvoiceLoading((prev) => new Set(prev).add(partnerKey))
    try {
      const res = await apiClient.get<InvoiceSummary[]>(
        `/liquidity-planning/plans/${planId}/partner-invoices?partnerCode=${encodeURIComponent(partnerKey)}`,
      )
      setInvoiceData((prev) => new Map(prev).set(partnerKey, res.data))
    } catch {
      loadedRef.current.delete(partnerKey)
    } finally {
      setInvoiceLoading((prev) => { const n = new Set(prev); n.delete(partnerKey); return n })
    }
  }, [planId])

  // Auto-load partner invoices whenever an account row is expanded
  useEffect(() => {
    for (const acctKey of expandedAccounts) {
      const sepIdx = acctKey.indexOf('__')
      if (sepIdx < 0) continue
      const rowCode = acctKey.slice(0, sepIdx)
      const sapAccount = acctKey.slice(sepIdx + 2)
      const row = rows.find((r) => r.code === rowCode)
      if (!row?.accountMonthly) continue
      for (const monthAccts of row.accountMonthly) {
        for (const acct of monthAccts) {
          if (acct.sapAccount === sapAccount) {
            for (const p of acct.partners) {
              void loadPartnerInvoices(p.partner)
            }
          }
        }
      }
    }
  }, [expandedAccounts, rows, loadPartnerInvoices])

  async function openInvoiceModal(docEntry: number, type: 'invoice' | 'credit') {
    setInvoiceModalLoading(true)
    try {
      const res = await apiClient.get<Record<string, unknown>>(
        `/liquidity-planning/plans/${planId}/invoice-detail?docEntry=${docEntry}&type=${type}`,
      )
      setInvoiceModal({ doc: res.data, type })
    } finally {
      setInvoiceModalLoading(false)
    }
  }

  async function openSapDocModal(docEntry: number, type: 'order' | 'quotation') {
    setSapDocModalLoading(true)
    try {
      const res = await apiClient.get<Record<string, unknown>>(
        `/liquidity-planning/plans/${planId}/invoice-detail?docEntry=${docEntry}&type=${type}`,
      )
      setSapDocModal({ doc: res.data, type })
    } finally {
      setSapDocModalLoading(false)
    }
  }

  function renderExpandedAccounts(row: PlanComparisonRow) {
    if (!row.accountMonthly) return null

    // Konten + Partner ueber alle Monate zusammenfuehren
    const accountMap = new Map<string, {
      name: string
      monthly: (number | null)[]
      partners: Map<string, { name: string; monthly: (number | null)[] }>
    }>()

    for (let mi = 0; mi < 12; mi++) {
      for (const acct of (row.accountMonthly[mi] ?? [])) {
        if (!accountMap.has(acct.sapAccount)) {
          accountMap.set(acct.sapAccount, { name: acct.name, monthly: Array(12).fill(null), partners: new Map() })
        }
        const acctEntry = accountMap.get(acct.sapAccount)!
        acctEntry.monthly[mi] = acct.ist

        for (const p of (acct.partners ?? [])) {
          if (!acctEntry.partners.has(p.partner)) {
            acctEntry.partners.set(p.partner, { name: p.name, monthly: Array(12).fill(null) })
          }
          acctEntry.partners.get(p.partner)!.monthly[mi] = p.amount
        }
      }
    }

    const accountRows = Array.from(accountMap.entries()).filter(
      ([, v]) => v.monthly.some((x) => x !== null && Math.abs(x) >= 0.01),
    )

    if (accountRows.length === 0) {
      return (
        <tr key={`${row.code}__empty`} className="bg-gray-50/60">
          <td colSpan={15} className="pl-10 py-1.5 text-caption text-gray-400 italic border-b border-gray-100">
            Keine SAP-Konten zugeordnet
          </td>
        </tr>
      )
    }

    return accountRows.flatMap(([sapAccount, { name, monthly, partners }]) => {
      const acctKey = `${row.code}__${sapAccount}`
      const isAcctExpanded = expandedAccounts.has(acctKey)
      const partnerRows = Array.from(partners.entries()).filter(
        ([, v]) => v.monthly.some((x) => x !== null && Math.abs(x) >= 0.01),
      )
      const hasPartners = partnerRows.length > 0

      const acctRow = (
        <tr key={acctKey} className="bg-indigo-50/30 hover:bg-indigo-50/60">
          <td className="sticky left-0 z-sticky bg-inherit border-r border-gray-200 pl-8 pr-2 py-1 text-caption text-gray-600" style={{ maxWidth: '280px', width: '280px' }}>
            <div className="flex items-center gap-1 min-w-0 overflow-hidden">
              {hasPartners ? (
                <button
                  type="button"
                  onClick={() => toggleAccount(acctKey)}
                  className="shrink-0 text-gray-400 hover:text-indigo-600 transition-colors"
                  title={isAcctExpanded ? 'Geschäftspartner einklappen' : 'Geschäftspartner aufklappen'}
                >
                  {isAcctExpanded
                    ? <ChevronDown className="size-3" />
                    : <ChevronRight className="size-3" />}
                </button>
              ) : (
                <span className="size-3 shrink-0" />
              )}
              <span className="font-mono text-gray-400 shrink-0">{sapAccount}</span>
              {name !== sapAccount && (
                <>
                  <span className="truncate ml-1">{name}</span>
                  <span title={name} className="shrink-0 ml-0.5 flex items-center cursor-help"><Info className="size-3 text-gray-300" /></span>
                </>
              )}
            </div>
          </td>
          {(() => {
            const total = annualSum(monthly)
            return (
              <td className="border-r border-gray-300 px-1.5 py-1 text-right text-caption tabular-nums font-medium text-gray-700 whitespace-nowrap bg-gray-100/70">
                {total === null || Math.abs(total) < 0.01
                  ? <span className="text-gray-300">–</span>
                  : EUR.format(total)}
              </td>
            )
          })()}
          {monthly.map((amount, i) => (
            <td key={i} className="border-r border-gray-100 px-1.5 py-1 text-right text-caption tabular-nums text-gray-500 whitespace-nowrap">
              {amount === null || Math.abs(amount) < 0.01 ? (
                <span className="text-gray-300">–</span>
              ) : (
                EUR.format(amount)
              )}
            </td>
          ))}
        </tr>
      )

      const partnerSubRows = isAcctExpanded ? partnerRows.flatMap(([partnerKey, p]) => {
        const invoiceRows: React.ReactNode[] = []
        const isLoading = invoiceLoading.has(partnerKey)
        const docs = invoiceData.get(partnerKey)
        const isPartnerExpanded = expandedPartners.has(partnerKey)
        const hasInvoices = docs !== undefined

        // Partner-Haupt-Zeile — Chevron zum Ein-/Ausklappen der Rechnungen
        const partnerRow = (
          <tr key={`${acctKey}__${partnerKey}`} className="bg-violet-50/40 hover:bg-violet-50/70">
            <td className="sticky left-0 z-sticky bg-inherit border-r border-gray-200 pl-14 pr-2 py-0.5 text-caption text-gray-500" style={{ maxWidth: '280px', width: '280px' }}>
              <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                {isLoading ? (
                  <Loader2 className="size-3 animate-spin shrink-0 text-violet-400" />
                ) : hasInvoices ? (
                  <button
                    type="button"
                    onClick={() => togglePartner(partnerKey)}
                    className="shrink-0 text-gray-400 hover:text-violet-600 transition-colors"
                    title={isPartnerExpanded ? 'Rechnungen einklappen' : 'Rechnungen aufklappen'}
                  >
                    {isPartnerExpanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
                  </button>
                ) : (
                  <span className="size-3 shrink-0 text-gray-300">↳</span>
                )}
                <span className="font-mono text-gray-400 shrink-0 ml-1">{partnerKey}</span>
                {p.name && p.name !== partnerKey && (
                  <>
                    <span className="truncate ml-1">{p.name}</span>
                    <span title={p.name} className="shrink-0 ml-0.5 flex items-center cursor-help"><Info className="size-3 text-gray-300" /></span>
                  </>
                )}
              </div>
            </td>
            {(() => {
              const total = annualSum(p.monthly)
              return (
                <td className="border-r border-gray-300 px-1 py-0.5 text-right text-caption font-medium text-gray-600 tabular-nums whitespace-nowrap bg-gray-100/60">
                  {total === null || Math.abs(total) < 0.01
                    ? <span className="text-gray-200">–</span>
                    : EUR.format(total)}
                </td>
              )
            })()}
            {p.monthly.map((amount, i) => {
              const hasAmount = amount !== null && Math.abs(amount) >= 0.01
              return (
                <td key={i} className="border-r border-gray-100 px-1 py-0.5 text-right text-caption text-gray-500 tabular-nums whitespace-nowrap">
                  {hasAmount ? EUR.format(amount) : <span className="text-gray-200">–</span>}
                </td>
              )
            })}
          </tr>
        )
        invoiceRows.push(partnerRow)

        // Rechnungszeilen — nur sichtbar wenn Partner aufgeklappt
        if (!isPartnerExpanded) return invoiceRows

        if (docs && docs.length === 0) {
          invoiceRows.push(
            <tr key={`${partnerKey}__none`} className="bg-violet-50/20">
              <td colSpan={15} className="pl-20 py-0.5 text-caption text-gray-400 italic border-b border-gray-100">
                Keine Rechnungen in diesem Jahr
              </td>
            </tr>,
          )
        } else if (docs) {
          for (const doc of docs) {
            const docMonthIdx = new Date(doc.DocDate).getMonth() // 0-indexed
            const statusLabel = doc.DocumentStatus === 'bost_Close' ? 'bezahlt' : 'offen'
            const statusColor = doc.DocumentStatus === 'bost_Close' ? 'text-green-600' : 'text-amber-600'
            invoiceRows.push(
              <tr key={`${partnerKey}__${doc.DocEntry}`} className="bg-violet-50/20 hover:bg-violet-100/40">
                <td className="sticky left-0 z-sticky bg-inherit border-r border-gray-200 pl-20 pr-2 py-0.5 text-caption text-gray-500" style={{ maxWidth: '280px', width: '280px' }}>
                  <button
                    type="button"
                    onClick={() => void openInvoiceModal(doc.DocEntry, doc.type)}
                    className="hover:underline text-left truncate block w-full"
                    disabled={invoiceModalLoading}
                    title={`${doc.type === 'credit' ? 'Gutschrift' : 'Rechnung'} ${doc.DocNum}`}
                  >
                    <span className="font-mono text-violet-600 mr-1">
                      {doc.type === 'credit' ? 'GS' : 'RE'} {doc.DocNum}
                    </span>
                    <span className="text-gray-400">{new Date(doc.DocDate).toLocaleDateString('de-DE')}</span>
                    {doc.DocDueDate && <span className="text-gray-300 ml-1">→ {new Date(doc.DocDueDate).toLocaleDateString('de-DE')}</span>}
                  </button>
                </td>
                <td className="border-r border-gray-300 bg-gray-100/40" />
                {Array.from({ length: 12 }, (_, ci) => {
                  if (ci !== docMonthIdx) return <td key={ci} className="border-r border-gray-100" />
                  return (
                    <td key={ci} className="border-r border-gray-100 px-1.5 py-0.5 text-right text-caption whitespace-nowrap">
                      <span className="tabular-nums text-violet-700">{EUR_INV.format(doc.DocTotal)}</span>
                      <span className={cn('ml-1 text-caption', statusColor)}>({statusLabel})</span>
                    </td>
                  )
                })}
              </tr>,
            )
          }
        }

        return invoiceRows
      }) : []

      return [acctRow, ...partnerSubRows]
    })
  }

  function renderExpandedOrders(row: PlanComparisonRow) {
    const docs: SapOpenDocument[] = row.openDocuments ?? []
    if (docs.length === 0) {
      return (
        <tr key={`${row.code}__empty`} className="bg-gray-50/60">
          <td colSpan={15} className="pl-10 py-1.5 text-caption text-gray-400 italic border-b border-gray-100">
            Keine offenen {row.code === 'ums_auftragsbestand' ? 'Aufträge' : 'Angebote'} im SAP-Cache
          </td>
        </tr>
      )
    }

    const typeLabel = row.code === 'ums_auftragsbestand' ? 'AT' : 'AN'
    const docType: 'order' | 'quotation' = row.code === 'ums_auftragsbestand' ? 'order' : 'quotation'
    const typeColor = row.code === 'ums_auftragsbestand' ? 'text-blue-600' : 'text-emerald-600'
    const rowBg = row.code === 'ums_auftragsbestand' ? 'bg-blue-50/30 hover:bg-blue-50/60' : 'bg-emerald-50/30 hover:bg-emerald-50/60'

    return docs.map((doc) => {
      const dueDate = doc.docDueDate ? new Date(doc.docDueDate) : null
      // Show amount in the correct month column only if docDueDate falls within plan year
      const docMonthIdx = dueDate && dueDate.getFullYear() === year ? dueDate.getMonth() : -1
      return (
        <tr key={`${row.code}__${doc.docEntry || doc.docNum}`} className={rowBg}>
          <td className="sticky left-0 z-sticky bg-inherit border-r border-gray-200 pl-8 pr-2 py-0.5 text-caption text-gray-500" style={{ maxWidth: '280px', width: '280px' }}>
            <div className="flex items-center gap-1 min-w-0 overflow-hidden">
              <button
                type="button"
                onClick={() => { if (doc.docEntry) void openSapDocModal(doc.docEntry, docType) }}
                className={cn('font-mono shrink-0 hover:underline cursor-pointer', typeColor)}
                title="Beleg öffnen"
              >
                {typeLabel} {doc.docNum}
              </button>
              {doc.docDate && <span className="text-gray-400 shrink-0 ml-1 text-[10px]">{new Date(doc.docDate).toLocaleDateString('de-DE')}</span>}
              {doc.cardName && (
                <>
                  <span className="truncate ml-1">{doc.cardName}</span>
                  <span title={`${doc.cardCode} ${doc.cardName}`} className="shrink-0 ml-0.5 flex items-center cursor-help"><Info className="size-3 text-gray-300" /></span>
                </>
              )}
            </div>
          </td>
          {Array.from({ length: 12 }, (_, ci) => {
            if (ci !== docMonthIdx) return <td key={ci} className="border-r border-gray-100" />
            return (
              <td key={ci} className="border-r border-gray-100 px-1.5 py-0.5 text-right text-caption whitespace-nowrap">
                <span className={cn('tabular-nums', typeColor)}>{EUR_INV.format(doc.docTotal)}</span>
              </td>
            )
          })}
        </tr>
      )
    })
  }

  function renderRow(row: PlanComparisonRow) {
    const indent = row.parentCode ? 'pl-6' : 'pl-2'
    const aggregateClass = row.isAggregate
      ? 'bg-gray-100 font-semibold border-t border-gray-300'
      : ''
    const isExpanded = expandedRows.has(row.code)
    // Auch Aggregat-Zeilen koennen einen Drilldown haben — z.B. liq_endbestand
    // (Bank-Konto-Aufschluesselung) oder kuenftig weitere computed-Positionen.
    // Daher pruefen wir auf accountMonthly/openDocuments unabhaengig vom isAggregate-Flag.
    const hasAccounts = (row.accountMonthly !== null || (row.openDocuments !== null && row.openDocuments.length > 0))

    return (
      <>
        <tr
          key={row.code}
          className={cn('hover:bg-blue-50/40', aggregateClass)}
          onMouseEnter={() => setHoveredRow(row.code)}
          onMouseLeave={() => setHoveredRow(null)}
        >
          <td className={cn('sticky left-0 z-sticky bg-inherit border-r border-gray-200 py-1.5 pr-2 text-small', indent)}>
            <div className="flex items-center gap-1">
              {hasAccounts ? (
                <button
                  type="button"
                  onClick={() => toggleExpand(row.code)}
                  className="shrink-0 text-gray-400 hover:text-blue-600 transition-colors"
                  title={isExpanded ? 'SAP-Konten einklappen' : 'SAP-Konten aufklappen'}
                >
                  {isExpanded
                    ? <ChevronDown className="size-3.5" />
                    : <ChevronRight className="size-3.5" />}
                </button>
              ) : (
                <span className="size-3.5 shrink-0" />
              )}
              {row.label}
            </div>
          </td>
          {(() => {
            const mode = getAggregateMode(row.code)
            const modeLabel = AGGREGATE_LABELS[mode]
            const tot = annualTotals(row.monthly, mode)
            const hasIst = tot.ist !== null
            const valueLabel = mode === 'sum'
              ? { ist: 'Jahres-IST (YTD)', soll: 'Jahres-SOLL (geplant, 12 Monate)', delta: 'Δ YTD' }
              : mode === 'first'
                ? { ist: 'IST Januar', soll: 'SOLL Januar', delta: 'Δ Januar' }
                : { ist: 'IST Dezember', soll: 'SOLL Dezember', delta: 'Δ Dezember' }
            // %-Bezugsgroesse fuer den Jahres-Wert: gleiche Logik wie pro Monat,
            // aber als Summe ueber die jeweils relevanten Monate (YTD fuer IST,
            // alle 12 fuer SOLL).
            const refKind = PERCENT_REFERENCE[row.code]
            const refSeries = refKind ? referenceBases[refKind] : null
            const baseSoll = refSeries
              ? refSeries.soll.reduce((s, v) => s + (v ?? 0), 0)
              : null
            const baseIstYtd = refSeries
              ? refSeries.ist.reduce<number>((s, v, i) =>
                  v !== null && row.monthly[i]?.ist !== null ? s + v : s, 0)
              : null
            const pctIst = formatPercent(tot.ist, baseIstYtd)
            const pctSoll = formatPercent(tot.soll, baseSoll)
            const tooltip = [
              modeLabel.tooltip,
              '',
              hasIst
                ? `${valueLabel.ist}: ${EUR.format(tot.ist!)}\n${valueLabel.soll}: ${EUR.format(tot.soll)}\n${valueLabel.delta}: ${tot.delta === null ? '—' : EUR.format(tot.delta)}`
                : `${valueLabel.soll}: ${EUR.format(tot.soll)}`,
            ].join('\n')
            return (
              <td
                className={cn(
                  'border-r border-gray-300 px-1.5 py-1 text-right text-xs tabular-nums whitespace-nowrap bg-gray-100/70',
                  row.isAggregate && 'font-semibold',
                )}
                title={tooltip}
              >
                {hasIst ? (
                  <div className="flex flex-col items-end leading-tight gap-px">
                    <span className="text-gray-800 font-semibold">
                      {EUR.format(tot.ist!)}
                      <span className="ml-1 text-[10px] font-normal text-gray-400">{modeLabel.letter}</span>
                    </span>
                    {pctIst && (
                      <span className="text-[10px] text-gray-500 font-normal">{pctIst}</span>
                    )}
                    <span className="text-[11px] text-gray-500">P {EUR.format(tot.soll)}</span>
                    {tot.delta !== null && tot.delta !== 0 && (
                      <span className={cn(
                        'text-[11px] font-medium',
                        tot.delta > 0 ? 'text-emerald-600/50' : 'text-red-500/50',
                      )}>
                        {tot.delta > 0 ? '+' : ''}{EUR.format(tot.delta)}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-end leading-tight gap-px">
                    <span className="text-blue-900 font-semibold">
                      {EUR.format(tot.soll)}
                      <span className="ml-1 text-[10px] font-normal text-blue-400">{modeLabel.letter}</span>
                    </span>
                    {pctSoll && (
                      <span className="text-[10px] text-blue-400 font-normal">{pctSoll}</span>
                    )}
                  </div>
                )}
              </td>
            )
          })()}
          {row.monthly.map((cell, i) => {
            const m = i + 1
            const closed = isMonthClosed(year, m)
            const isEditing = editing?.position === row.code && editing.month === m
            // Aggregat-Zeilen sind weiterhin nicht editierbar (werden gerechnet);
            // abgeschlossene Monate dagegen JA — Doppelklick auf einen geschlossenen
            // Monat ueberschreibt das gespeicherte SOLL nachtraeglich (z.B. fuer
            // Plan-Korrekturen / Re-Forecasts).
            const isEditable = !row.isAggregate
            const cellBg = closed ? 'bg-gray-50/40' : ''
            return (
              <td
                key={i}
                className={cn(
                  'border-r border-gray-100 px-1.5 py-1 text-right text-xs tabular-nums whitespace-nowrap',
                  cellBg,
                  isEditable && 'cursor-pointer hover:bg-blue-100/50',
                )}
                onDoubleClick={(e) => {
                  e.stopPropagation()
                  if (!isEditable) return
                  setEditing({ position: row.code, month: m, value: String(cell.override ?? cell.base) })
                }}
                title={
                  closed
                    ? `IST aus SAP: ${cell.ist === null ? '—' : EUR.format(cell.ist)}\nGeplant war: ${EUR.format(cell.soll)}\nΔ: ${cell.delta === null ? '—' : EUR.format(cell.delta)}\nDoppelklick: SOLL nachtraeglich aendern`
                    : `SOLL: ${EUR.format(cell.soll)}\nBasis: ${EUR.format(cell.base)} | Adjustment: ${EUR.format(cell.adjustment)} | Override: ${cell.override !== null ? EUR.format(cell.override) : '—'}`
                }
              >
                {isEditing ? (
                  <div className="inline-flex flex-col items-end">
                    <input
                      autoFocus
                      type="number"
                      step="100"
                      value={editing.value}
                      onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                      onBlur={() => commitEdit(row.code, m, editing.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.shiftKey) {
                          e.preventDefault()
                          void commitEditFill(row.code, m, editing.value)
                        } else if (e.key === 'Enter') {
                          e.preventDefault()
                          void commitEdit(row.code, m, editing.value)
                        }
                        if (e.key === 'Escape') {
                          setEditing(null)
                          setEditError(null)
                        }
                      }}
                      title="Enter: nur diesen Monat speichern · Escape: abbrechen · Shift+Enter: bis Dez kopieren"
                      className="w-24 text-right border border-blue-400 rounded px-1 py-0.5"
                    />
                    {m < 12 && (
                      <div className="flex gap-1 mt-0.5">
                        <button
                          type="button"
                          // onMouseDown statt onClick: feuert vor onBlur des Inputs,
                          // preventDefault verhindert den Focus-Wechsel — sonst wuerde
                          // erst commitEdit (nur dieser Monat) laufen, der Button danach
                          // ins Leere klicken.
                          onMouseDown={(e) => {
                            e.preventDefault()
                            void commitEditFill(row.code, m, editing.value, m + 1)
                          }}
                          title={`Wert in ${MONTH_LABELS[m - 1]} und ${MONTH_LABELS[m]} setzen`}
                          className="px-1 py-px text-[10px] leading-none rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          → {MONTH_LABELS[m]}
                        </button>
                        {m < 11 && (
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              void commitEditFill(row.code, m, editing.value)
                            }}
                            title={`Wert in alle ${12 - m + 1} Monate von ${MONTH_LABELS[m - 1]} bis Dez setzen (Shortcut: Shift+Enter)`}
                            className="px-1 py-px text-[10px] leading-none rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                          >
                            → Dez
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : closed ? (() => {
                  const refKind = PERCENT_REFERENCE[row.code]
                  const refSeries = refKind ? referenceBases[refKind] : null
                  const pctIst = formatPercent(cell.ist, refSeries?.ist[i] ?? null)
                  return (
                    <div className="flex flex-col items-end leading-tight gap-px">
                      <span className="text-gray-800 font-medium">
                        {cell.ist === null ? '–' : EUR.format(cell.ist)}
                      </span>
                      {pctIst && (
                        <span className="text-[10px] text-gray-500 font-normal">{pctIst}</span>
                      )}
                      <span className="text-[11px] text-gray-400">
                        P {cell.soll === null ? '–' : EUR.format(cell.soll)}
                      </span>
                      {cell.ist !== null && (() => {
                        const d = cell.ist - cell.soll
                        if (d === 0) return null
                        return (
                          <span className={cn(
                            'text-[11px] font-medium',
                            d > 0 ? 'text-emerald-600/50' : 'text-red-500/50',
                          )}>
                            {d > 0 ? '+' : ''}{EUR.format(d)}
                          </span>
                        )
                      })()}
                      {/* Plausi-Check fuer cf_ust_zahllast: Vergleich gegen GL-
                          Verrechnungskonten. Δ wird subtil amber gezeigt
                          wenn die Differenz nennenswert ist (>1 €). */}
                      {row.code === 'cf_ust_zahllast' && row.glCheck && (() => {
                        const gl = row.glCheck.zahllastByMonth[i] ?? 0
                        if (cell.ist === null) return null
                        const d = gl - cell.ist
                        if (Math.abs(d) < 1) return null
                        return (
                          <span
                            className="text-[10px] text-amber-600 italic"
                            title={`Plausi-Check vs. SAP-GL: USt-Schuld-Konto ${row.glCheck.payableAccount} − Vorsteuer-Konto ${row.glCheck.inputAccount} = ${EUR.format(gl)} (Δ zur Berechnung: ${d > 0 ? '+' : ''}${EUR.format(d)})`}
                          >
                            GL Δ {d > 0 ? '+' : ''}{EUR.format(d)}
                          </span>
                        )
                      })()}
                      {/* Plausi-Check fuer Liqui-Positionen: Bank-Wahrheit (= IST)
                          gegen P&L-derivierte Kette aus Aggregator. Δ deutet auf
                          Reconciliation-Themen hin (Wage-Konto, Geldtransit etc.). */}
                      {row.chainCheck && cell.ist !== null && (() => {
                        const chain = row.chainCheck.chainValueByMonth[i] ?? 0
                        const d = chain - cell.ist
                        if (Math.abs(d) < 1) return null
                        return (
                          <span
                            className="text-[10px] text-amber-600 italic"
                            title={`${row.chainCheck.label}: ${EUR.format(chain)} — Bank-Realitaet: ${EUR.format(cell.ist)} — Δ ${d > 0 ? '+' : ''}${EUR.format(d)} (Hinweis auf Buchungen ausserhalb der GuV-Kette: Wage-Konto, Geldtransit, USt-Verrechnung etc.)`}
                          >
                            P&L Δ {d > 0 ? '+' : ''}{EUR.format(d)}
                          </span>
                        )
                      })()}
                    </div>
                  )
                })() : (() => {
                  const refKind = PERCENT_REFERENCE[row.code]
                  const refSeries = refKind ? referenceBases[refKind] : null
                  const pctSoll = formatPercent(cell.soll, refSeries?.soll[i] ?? null)
                  return (
                    <div className="flex flex-col items-end leading-tight gap-px">
                      <span className={cn(
                        cell.override !== null ? 'font-semibold text-blue-700' : 'text-blue-900',
                      )}>
                        {cell.soll === null ? '–' : EUR.format(cell.soll)}
                      </span>
                      {pctSoll && (
                        <span className="text-[10px] text-blue-400 font-normal">{pctSoll}</span>
                      )}
                    </div>
                  )
                })()}
              </td>
            )
          })}
          {/* Per-Zeile Recalc-Button — nur fuer Blatt-Positionen, nicht Aggregate */}
          {!row.isAggregate ? (
            <td className="border-r border-gray-100 px-0.5 py-1 text-center">
              <button
                type="button"
                onClick={() => void handlePositionRecalc(row.code)}
                disabled={recalcingMonth !== null || recalcingPosition !== null}
                title={`Nur „${row.label}" neu berechnen`}
                className={cn('transition-opacity text-gray-300 hover:text-blue-500 disabled:cursor-not-allowed', hoveredRow === row.code ? 'opacity-100' : 'opacity-0')}
              >
                <RefreshCw className={cn('size-3', recalcingPosition === row.code && 'animate-spin text-blue-500')} />
              </button>
            </td>
          ) : (
            <td className="border-r border-gray-100" />
          )}
        </tr>
        {isExpanded && row.openDocuments !== null
          ? renderExpandedOrders(row)
          : isExpanded && renderExpandedAccounts(row)}
      </>
    )
  }

  function renderSection(label: string, sectionRows: PlanComparisonRow[]) {
    if (sectionRows.length === 0) return null
    return (
      <>
        <tr className="bg-blue-50">
          <td colSpan={15} className="sticky left-0 px-2 py-1.5 text-h5 text-blue-900 font-semibold">{label}</td>
        </tr>
        {sectionRows.map(renderRow)}
      </>
    )
  }

  return (
    <Card>
      {editError && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-start gap-2">
          <span className="text-small text-red-800 flex-1">{editError}</span>
          <button
            type="button"
            onClick={() => setEditError(null)}
            className="text-red-600 hover:text-red-800 text-small font-medium"
          >
            schliessen
          </button>
        </div>
      )}
      <Card.Body className="p-0 overflow-auto">
        <table className="w-full border-separate border-spacing-0 table-fixed">
          <colgroup>
            <col className="w-[200px]" />
            <col className="w-[110px]" />
            {MONTH_LABELS.map((m) => (
              <col key={m} />
            ))}
            <col className="w-[32px]" />
          </colgroup>
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 top-0 z-sticky bg-gray-50 text-left px-2 py-2 text-caption uppercase border-b border-r border-gray-200">
                Position
              </th>
              <th className="sticky top-0 bg-gray-100 px-1.5 py-2 text-right text-caption uppercase border-b border-r border-gray-300 font-semibold text-gray-700">
                Jahr
              </th>
              {MONTH_LABELS.map((m, i) => (
                <th key={m} className="group sticky top-0 bg-gray-50 px-1.5 py-2 text-right text-caption uppercase border-b border-r border-gray-100">
                  <span className="relative inline-flex items-center justify-end gap-1 w-full">
                    {m}
                    <button
                      type="button"
                      onClick={() => void handleMonthRecalc(i + 1)}
                      disabled={recalcingMonth !== null || recalcingPosition !== null}
                      title={`Nur ${m} neu berechnen (SAP-Daten werden nicht neu abgerufen)`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-gray-400 hover:text-blue-600 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={cn('size-3', recalcingMonth === i + 1 && 'animate-spin text-blue-500')} />
                    </button>
                  </span>
                </th>
              ))}
              <th className="sticky top-0 bg-gray-50 border-b border-r border-gray-100" />
            </tr>
          </thead>
          <tbody>
            {renderSection('Gewinn- und Verlustrechnung (BWA)', sections.guv)}
            {renderSection('Bilanz', sections.bilanz)}
            {renderSection('Cashflow-Ableitung (Indirekt)', sections.cf)}
            {renderSection('Liquiditätsentwicklung', sections.li)}
          </tbody>
        </table>
      </Card.Body>
      <Card.Footer>
        <div className="text-caption text-gray-500">
          Spalte „Jahr": Aggregat-Modus pro Position — kleines Kürzel hinter dem Wert: <strong>S</strong> = Summe der 12 Monate (Default, für Flows wie Cashflow/GuV), <strong>F</strong> = First (Januar-Wert, für Bestände am Jahresanfang), <strong>L</strong> = Last (Dezember-Wert, für Bestände am Jahresende). Mouseover zeigt die genaue Erläuterung. Abgeschlossene Monate (grau): IST (oben), SOLL (P, klein) und Δ farbig direkt in der Zelle. Offene Monate (blau): geplantes SOLL. Doppelklick auf eine Zelle (offen ODER abgeschlossen): SOLL überschreiben — <strong>Enter</strong> speichert nur diesen Monat, der Button <strong>→ &lt;Folgemonat&gt;</strong> kopiert den Wert auch in den nächsten Monat, <strong>→ Dez</strong> bzw. <strong>Shift+Enter</strong> füllt alle Folgemonate bis Dezember. Pfeil an der Position: SAP-Konten aufklappen (bei Auftragsbestand/Angebotsbestand: offene Aufträge/Angebote). Pfeil am Konto: Geschäftspartner aufklappen (Rechnungen laden im Hintergrund). Pfeil am Partner: Rechnungen ein-/ausklappen. Rechnungs-/Angebots-/Auftragsnummer anklicken: Beleg öffnen.
        </div>
      </Card.Footer>

      <Modal
        open={invoiceModal !== null}
        onOpenChange={(open) => { if (!open) setInvoiceModal(null) }}
        title={invoiceModal
          ? `${invoiceModal.type === 'credit' ? 'Gutschrift' : 'Rechnung'} ${String(invoiceModal.doc['DocNum'] ?? '')}`
          : ''}
        size="xl"
      >
        {invoiceModal && (
          <SapDocumentView doc={invoiceModal.doc} type={invoiceModal.type === 'credit' ? 'credit' : 'invoice'} />
        )}
      </Modal>

      <Modal
        open={sapDocModal !== null || sapDocModalLoading}
        onOpenChange={(open) => { if (!open) setSapDocModal(null) }}
        title={sapDocModal
          ? `${sapDocModal.type === 'order' ? 'Auftrag' : 'Angebot'} ${String(sapDocModal.doc['DocNum'] ?? '')}`
          : sapDocModalLoading ? 'Beleg wird geladen…' : ''}
        size="xl"
      >
        {sapDocModalLoading && (
          <div className="flex items-center justify-center py-16 text-gray-500">Beleg wird geladen…</div>
        )}
        {sapDocModal && !sapDocModalLoading && (
          <SapDocumentView doc={sapDocModal.doc} type={sapDocModal.type} />
        )}
      </Modal>
    </Card>
  )
}

